import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../common/Card';
import { Button } from '../common/Button';
import { Plus, Edit, ArrowRight } from 'lucide-react';
import { subscriptionService } from '../../services/subscriptionService';
import { AddSubscriptionModal } from '../modals/AddSubscriptionModal';
import { getSubscriptionIcon, getCategoryColor } from '../../utils/iconUtils';
import { FilterBar } from './FilterBar';
import { SubscriptionSkeleton } from '../skeletons/SubscriptionSkeleton';
import { useNavigate } from 'react-router-dom';
import { CURRENCY_SYMBOLS } from '../../config/currencies';
import { useAnalytics } from '../../hooks/useAnalytics';

const getStatusBadgeStyle = (status) => {
    switch (status?.toLowerCase()) {
        case 'active':
            return 'bg-green-50 text-green-700 border-green-200';
        case 'paused':
            return 'bg-yellow-50 text-yellow-700 border-yellow-200';
        case 'cancelled':
            return 'bg-red-50 text-red-700 border-red-200';
        default:
            return 'bg-gray-50 text-gray-700 border-gray-200';
    }
};

export const SubscriptionsList = ({ subscriptions, onSubscriptionUpdate, isLoading, limit }) => {
    const navigate = useNavigate();
    const { trackEvent, events } = useAnalytics();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSubscription, setEditingSubscription] = useState(null);
    const [filters, setFilters] = useState({
        search: '',
        sortBy: 'date-asc'
    });

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        trackEvent(events.FEATURE.FILTER_APPLY, 'feature', {
            filters: newFilters
        });
    };

    const filterSubscriptions = (subs) => {
        return subs.filter(sub => {
            // Search filter only
            if (filters.search && !sub.service_name.toLowerCase().includes(filters.search.toLowerCase())) {
                return false;
            }
            return true;
        }).sort((a, b) => {
            // Sort by date only
            return filters.sortBy === 'date-asc'
                ? new Date(a.next_billing_date) - new Date(b.next_billing_date)
                : new Date(b.next_billing_date) - new Date(a.next_billing_date);
        });
    };

    const filteredSubscriptions = filterSubscriptions(subscriptions);

    const handleOpenModal = (subscription = null) => {
        setEditingSubscription(subscription);
        setIsModalOpen(true);
        trackEvent(events.FEATURE.SUBSCRIPTION_ADD, 'feature', {
            action: subscription ? 'edit' : 'add'
        });
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingSubscription(null);
    };

    const handleDelete = async (id) => {
        try {
            await subscriptionService.deleteSubscription(id);
            trackEvent(events.FEATURE.SUBSCRIPTION_DELETE, 'feature', {
                subscription_id: id
            });
            onSubscriptionUpdate();
        } catch (error) {
            console.error('Error deleting subscription:', error);
            trackEvent(events.SYSTEM.ERROR, 'system', {
                action: 'delete_subscription',
                error: error.message,
                subscription_id: id
            });
        }
    };

    const groupSubscriptionsByCategory = (subs) => {
        return subs.reduce((acc, sub) => {
            const category = sub.category || 'Uncategorized';
            if (!acc[category]) {
                acc[category] = [];
            }
            acc[category].push(sub);
            return acc;
        }, {});
    };

    const renderSubscriptions = () => {
        if (isLoading) {
            return Array(limit ? Math.min(3, limit) : 3).fill(0).map((_, index) => (
                <SubscriptionSkeleton key={index} />
            ));
        }

        const groupedSubscriptions = groupSubscriptionsByCategory(filteredSubscriptions);
        let itemCount = 0;

        return Object.entries(groupedSubscriptions).map(([category, subs]) => {
            // If we're limiting items, skip empty categories
            if (limit && itemCount >= limit) return null;

            // For limited view, only show a portion of subscriptions
            const displaySubs = limit ? subs.slice(0, limit - itemCount) : subs;
            itemCount += displaySubs.length;

            return (
                <div key={category} className="space-y-4">
                    <div className="flex items-center gap-2">
                        <h3 className="text-sm font-medium text-gray-500">
                            {category.toUpperCase()}
                        </h3>
                        <div className="h-[1px] flex-1 bg-gray-200" />
                        <span className="text-sm text-gray-400">
                            {CURRENCY_SYMBOLS[subs[0]?.currency] || '$'}
                            {subs.reduce((sum, sub) => sum + sub.cost, 0).toFixed(2)}/month
                        </span>
                    </div>
                    <div className="space-y-3 mb-6">
                        {displaySubs.map((sub) => {
                            const Icon = getSubscriptionIcon(sub.service_name, sub.category);
                            const bgColor = getCategoryColor(sub.category);

                            return (
                                <div key={sub.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-white rounded-lg shadow gap-4">
                                    <div className="flex items-center w-full sm:w-auto">
                                        <div className={`w-10 h-10 rounded-full ${bgColor} flex items-center justify-center mr-4 flex-shrink-0`}>
                                            <Icon className="h-5 w-5 text-gray-700" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="font-medium truncate">{sub.service_name}</span>
                                                <span className={`text-xs px-2 py-0.5 rounded-full border ${getStatusBadgeStyle(sub.status)}`}>
                                                    {sub.status?.charAt(0).toUpperCase() + sub.status?.slice(1)}
                                                </span>
                                            </div>
                                            <div className="text-sm text-gray-500 truncate">
                                                Next renewal: {sub.next_billing_date ?
                                                    new Date(sub.next_billing_date).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    }) : 'Date not available'
                                                }
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between w-full sm:w-auto">
                                        <div className="text-left sm:text-right sm:mr-4">
                                            <div className="font-bold">
                                                {CURRENCY_SYMBOLS[sub.currency] || '$'}{sub.cost.toFixed(2)}
                                            </div>
                                            <div className="text-sm text-gray-500">{sub.subscription_type}</div>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleOpenModal(sub)}
                                            >
                                                <Edit className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="destructive"
                                                size="sm"
                                                onClick={() => handleDelete(sub.id)}
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }).filter(Boolean); // Remove null entries
    };

    const handleViewAll = () => {
        trackEvent(events.FEATURE.PAGE_VIEW, 'feature', {
            action: 'view_all_subscriptions',
            from: 'subscription_list'
        });
        navigate('/subscriptions');
    };

    return (
        <>
            <Card className="w-full">
                <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <CardTitle>Your Subscriptions</CardTitle>
                    <Button
                        size="sm"
                        onClick={() => handleOpenModal()}
                        disabled={isLoading}
                        className="hover:scale-105 transition-transform duration-200 w-full sm:w-auto"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Add New
                    </Button>
                </CardHeader>
                <CardContent>
                    <FilterBar
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        disabled={isLoading}
                    />
                    <div className="space-y-6 mt-6">
                        {renderSubscriptions()}
                    </div>

                    {limit && filteredSubscriptions.length > limit && (
                        <div className="mt-6 pt-6 border-t border-gray-100">
                            <Button
                                variant="outline"
                                className="w-full bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 transition-all duration-200 group shadow-sm hover:shadow-md"
                                onClick={handleViewAll}
                            >
                                <span className="flex items-center justify-center gap-2">
                                    View All Subscriptions
                                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
                                </span>
                                <span className="text-sm text-gray-500 block ml-2">
                                    {filteredSubscriptions.length - limit} more items
                                </span>
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            <AddSubscriptionModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSuccess={onSubscriptionUpdate}
                subscription={editingSubscription}
            />
        </>
    );
}; 